import { Injectable } from "@angular/core";
import { CONSTANT } from "src/app/constant";
import { FinalRatingsData, OBCommentsReq } from "src/app/models/models";
import { ratingScale } from "src/app/shared/constants/rating-scales";
import { ContainerRatingsService } from "../ratings/container-ratings-form/services/base/container-ratings.services";
import { resolveTs1RatingCalcKey } from "../ratings/container-ratings-form/ts1-rating-form.registry";
import {
	buildLastObResetTimeMap,
	filterAddRemoveObForRating,
} from "../ratings/container-ratings-form/shared-v2/utils/observation.utils";
import {
	buildTs1V3FullyDisabledObKeys,
	buildTs1V3NaRoleObKeys,
	includeTs1V3ObInRatingNewObs,
	isTs1V3ConfigObsExcludedByNa,
	mergeTs1V3ObEventsForRatingCalc,
} from "../ratings/container-ratings-form/ts1-rating-form-v3/utils/ts1-rating-ab-ob-merge.utils";
import { OfflineRatingV2Calculator } from "./offline-rating-calculation-v2.service";
import { OfflineRatingV4Calculator } from "../ratings/container-ratings-form/ts1-rating-form-v4";
import { resolveOriginalObsCodesFromAssessmentConfig } from "../ratings/container-ratings-form/ts1-rating-form-v4/calculation/original-obs-codes.utils";
import {
	isTs1V3OrV4RatingFormVersion,
	isTs1V4RatingFormVersion,
} from "../ratings/container-ratings-form/shared-v2/utils/rating-form-version.utils";

export interface IObservationRating {
	observationCode: string;
	maneuverCode: string;
	totalAttempt: number;
	activeAttempt: number;
	isRemoved: boolean | null;
	/** PF/PM seat when present — How Often keys by role so dual presets are not collapsed. */
	candidateRole?: string | null;
}

export interface ITemRatingCalculationParams {
	manueverCode: string;
	maneuverDescription: string;
	rate: number;
	assessmentId: string;
}

export interface IDisabledObservation {
	maneuverCode: string;
	maneuverId: string;
	observationCode: string;
	observationId: string;
}

export interface IPreviewDisabledObservation extends IDisabledObservation {
	candidateId: string;
}
@Injectable({
	providedIn: "root",
})
export class OfflineRatingService {
	private readonly calc = new OfflineRatingV2Calculator();
	private readonly ts1V3Calc = new OfflineRatingV2Calculator({ formVersion: 3 });
	private readonly ts1V4Calc = new OfflineRatingV4Calculator();

	constructor(private draftService: ContainerRatingsService) {}

	private resolveV2Calculator(): OfflineRatingV2Calculator | OfflineRatingV4Calculator {
		switch (resolveTs1RatingCalcKey(this.draftService.draft?.ratingFormVersion)) {
			case "v4":
				return this.ts1V4Calc;
			case "v3":
				return this.ts1V3Calc;
			default:
				return this.calc;
		}
	}

	private isEligibleFormType(formTypeCode?: string): boolean {
		if (!formTypeCode) return true;
		return formTypeCode === CONSTANT.FORM_TYPE.ts_1 || formTypeCode === CONSTANT.FORM_TYPE.ts_3;
	}

	calculate(candidateId: string): FinalRatingsData {
		// gets the selected draft
		const draft = this.draftService.draft;

		console.log("apakah draft?", draft);
		// init default return value
		let result: FinalRatingsData = {
			ratingA: 1,
			ratingB: 1,
			ratingC: 1,
			finalRating: 1,
		};

		if (draft) {
			const candidateRating = draft?.candidateRating;

			console.log("candidate rating", candidateRating);

			if (candidateRating) {
				const temRate = candidateRating?.temRate ?? {};
				const temScores = [];

				for (const key in temRate) {
					const cId = key.split(".")[0];
					const data = temRate[key];

					if (cId === candidateId) {
						temScores.push({
							...data,
							candidateId: cId,
						});
					}
				}

				// exclude maneuver rated 0 and -1
				const { zeroRated: excludeManeuverIds, nonZeroRated } = temScores.reduce<{
					zeroRated: string[];
					nonZeroRated: number[];
				}>(
					(acc, prevValue) => {
						if (prevValue.rate === 0 || prevValue.rate === -1 || prevValue.rate === -2) {
							acc.zeroRated.push(prevValue.assessmentId);
						} else {
							acc.nonZeroRated.push(prevValue.rate);
						}
						return acc;
					},
					{ zeroRated: [], nonZeroRated: [] },
				);

				console.log("zeroRated", excludeManeuverIds, nonZeroRated);

				// obs history — drop repeat-stale rows and events before latest per-OB isReset
				// (see filterAddRemoveObForRating / docs/business/ratings.md — OB undo and rating calculation)
				const lastObResetTimeMap = buildLastObResetTimeMap(candidateRating?.addRemoveOb ?? [], candidateId);
				const observationHistoryUnfiltered = filterAddRemoveObForRating(
					candidateRating?.addRemoveOb ?? [],
					candidateId,
					lastObResetTimeMap,
				).sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

				const isTs1V3OrV4 = isTs1V3OrV4RatingFormVersion(draft?.ratingFormVersion);

				const naRoleKeys = isTs1V3OrV4
					? buildTs1V3NaRoleObKeys(observationHistoryUnfiltered)
					: new Set<string>();

				// Latest event per maneuver+observation wins (reset row carries restored activeAttempt/totalAttempt).
				// TS1-v3: prefer any still-active non-N/A PF/PM side so PF-only N/A does not drop PM counts.
				const observationHistory = (
					isTs1V3OrV4
						? mergeTs1V3ObEventsForRatingCalc(observationHistoryUnfiltered).filter(
								includeTs1V3ObInRatingNewObs,
							)
						: (() => {
								const observationHistoryMap: { [id: string]: OBCommentsReq } = {};
								for (const o of observationHistoryUnfiltered) {
									observationHistoryMap[`${o.maneuverCode}.${o.observationCode}`] =
										o as OBCommentsReq;
								}
								return Object.values(observationHistoryMap);
							})()
				) as OBCommentsReq[];

				const observationDisabled = isTs1V3OrV4
					? [...buildTs1V3FullyDisabledObKeys(observationHistoryUnfiltered)].map((key) => {
							const sep = key.indexOf("::");
							return {
								maneuverCode: key.slice(0, sep),
								observationCode: key.slice(sep + 2),
							} as OBCommentsReq;
						})
					: observationHistory.filter((data) => {
							return data.isDisabled == null || data.isDisabled == true;
						});

				const form = draft?.formDetails;
				const assessment = form?.assessmentConfig ?? [];

				// get all predefined OBs except from the 0-rated maneuver
				const meneuverCodeToExclude: string[] = [];
				const [, predefinedObservations] = assessment.reduce<
					[
						string[],
						{
							id: string;
							categoryId: string;
							totalAttempt: number;
							activeAttempt: number;
							observationCode: string;
							maneuverCode: string;
							candidateRole?: string | null;
						}[],
					]
				>(
					(acc, { maneuverType, observations, assessmentId }) => {
						if (excludeManeuverIds.includes(assessmentId)) {
							meneuverCodeToExclude.push(maneuverType.code);
						}

						if (!excludeManeuverIds.includes(assessmentId)) {
							observations.map((observation) => {
								if (
									isTs1V3OrV4 &&
									isTs1V3ConfigObsExcludedByNa({
										maneuverCode: maneuverType.code,
										observationCode: observation.code,
										candidateRole: observation.candidateRole,
										naRoleKeys,
									})
								) {
									return;
								}
								acc[0].push(observation.code);
								acc[1].push({
									id: observation.code,
									categoryId: `${maneuverType.code}.${observation.category.code}`,
									totalAttempt: observation.attempt ?? 1,
									activeAttempt: observation.attempt ?? 1,
									observationCode: observation.code,
									maneuverCode: maneuverType.code,
									candidateRole: observation.candidateRole ?? null,
								});
							});
						}
						return acc;
					},
					[[], []],
				);

				// get the total of additional obs added
				const observationHistoryAltered = observationHistory.map((observation) => {
					return {
						id: observation.observationCode,
						maneuver: observation.maneuverCode,
						totalAttempt: observation.totalAttempt ?? 1,
						activeAttempt: observation.activeAttempt ?? 1,
						observationCode: observation.observationCode,
						maneuverCode: observation.maneuverCode,
						isRemoved: observation.isRemoved,
						candidateRole: observation.candidateRole ?? null,
					};
				});

				console.log("observationHistoryAltered", observationHistoryAltered);

				const PredefinedObs = predefinedObservations?.map((v) => {
					return {
						observationCode: v.observationCode,
						maneuverCode: v.maneuverCode,
						totalAttempt: v.totalAttempt,
						activeAttempt: v.activeAttempt,
						isRemoved: false,
						candidateRole: v.candidateRole ?? null,
					};
				});

				console.log("predefinedObs", PredefinedObs);

				const NewObs = observationHistoryAltered?.map((v) => {
					return {
						observationCode: v.observationCode,
						maneuverCode: v.maneuverCode,
						totalAttempt: v.totalAttempt,
						activeAttempt: v.activeAttempt,
						isRemoved: v.isRemoved,
						candidateRole: v.candidateRole ?? null,
					};
				});

				console.log("NewObs", NewObs);

				const formTypeCode = draft?.formDetails?.formType?.code;

				const v4OriginalObsCodes = isTs1V4RatingFormVersion(draft?.ratingFormVersion)
					? resolveOriginalObsCodesFromAssessmentConfig(assessment)
					: undefined;

				const ratingInput = {
					maneuverCodeToExclude: [...new Set(meneuverCodeToExclude)],
					predefinedObs: [...PredefinedObs],
					newObs: [...NewObs],
					observationDisabled,
					formTypeCode,
					...(v4OriginalObsCodes ? { originalObsCodes: v4OriginalObsCodes } : {}),
				};

				const useV2 = this.isEligibleFormType(formTypeCode);

				// rating A
				const ratingA = useV2 ? this.resolveV2Calculator().calculateRatingA(ratingInput) : this.calculateRatingA(ratingInput);

				// rating B
				const ratingB = useV2 ? this.resolveV2Calculator().calculateRatingB(ratingInput) : this.calculateRatingB(ratingInput);

				const ratingC = this.calculateRatingC(nonZeroRated);

				// Get form type to check if it's a cabin crew form
				const formType = draft?.formDetails?.formType?.code;
				const isCabinCrewForm =
					formType === CONSTANT.FORM_TYPE.cabin_crew || formType === CONSTANT.FORM_TYPE.cabin_crew_instructor;

				// For cabin crew forms only: If any rating is 1, final rating should default to 1
				let finalRating: number;
				if (isCabinCrewForm && (ratingA === 1 || ratingB === 1 || ratingC === 1)) {
					finalRating = 1;
				} else {
					finalRating = Math.min(ratingA, ratingB, ratingC);
				}

				// Ensure finalRating is a valid number, default to 1 if invalid
				if (!Number.isFinite(finalRating) || finalRating == null) {
					finalRating = 1;
				}

				result = { ratingA, ratingB, ratingC, finalRating };
			} else {
				console.error(`No rating to be calculated for this draft.`);
			}
		} else {
			console.error(`No selected draft or draft does not exists`);
		}

		return result;
	}

	calculateRatingA = ({
		newObs = [],
		predefinedObs = [],
		maneuverCodeToExclude = [],
		observationDisabled = [],
	}: {
		predefinedObs: IObservationRating[];
		newObs: IObservationRating[];
		maneuverCodeToExclude?: string[];
		observationDisabled: OBCommentsReq[];
	}) => {
		//to exclude maneuver
		const filteredPredefinedObs = predefinedObs?.filter(({ maneuverCode, observationCode }) => {
			const isDisabledOb = observationDisabled.find((data) => {
				return data.observationCode == observationCode && data.maneuverCode == maneuverCode;
			});
			return !maneuverCodeToExclude?.includes(maneuverCode) && !isDisabledOb;
		});

		const filteredNewObs = newObs?.filter(({ maneuverCode, observationCode }) => {
			const isDisabledOb = observationDisabled.find((data) => {
				return data.observationCode == observationCode && data.maneuverCode == maneuverCode;
			});

			return !maneuverCodeToExclude?.includes(maneuverCode) && !isDisabledOb;
		});

		// only get unique values; remove duplicate obs from different assessments
		const predefinedObservationCodes = filteredPredefinedObs?.map((v) => v.observationCode);
		const uniquePredefinedObservationCodes = [...new Set(predefinedObservationCodes)];

		// get the total of additional obs added
		const _additionalObs =
			filteredNewObs
				.filter(({ observationCode }) => !uniquePredefinedObservationCodes.includes(observationCode))
				?.filter(Boolean)?.length ?? 0;

		const _predefinedObservationCount = predefinedObservationCodes?.length ?? 0;

		const _removedObsCount = filteredNewObs.filter(({ isRemoved }) => isRemoved)?.length ?? 0;

		const totalObs = _additionalObs + _predefinedObservationCount;
		const _ratingA = Math.round(((totalObs - _removedObsCount) / totalObs) * 100);
		return ratingScale(_ratingA);
	};

	calculateRatingB = ({
		newObs = [],
		predefinedObs = [],
		maneuverCodeToExclude = [],
		observationDisabled = [],
	}: {
		predefinedObs: IObservationRating[];
		newObs: IObservationRating[];
		maneuverCodeToExclude?: string[];
		observationDisabled: OBCommentsReq[];
	}) => {
		console.log("Maneuver to exclude from calculation : ", maneuverCodeToExclude?.join(","));

		//to exclude maneuver
		const filteredPredefinedObs = predefinedObs?.filter(({ maneuverCode, observationCode }) => {
			const isDisabledOb = observationDisabled.find((data) => {
				return data.observationCode == observationCode && data.maneuverCode == maneuverCode;
			});
			return !maneuverCodeToExclude?.includes(maneuverCode) && !isDisabledOb;
		});
		const filteredNewObs = newObs?.filter(({ maneuverCode, observationCode }) => {
			const isDisabledOb = observationDisabled.find((data) => {
				return data.observationCode == observationCode && data.maneuverCode == maneuverCode;
			});

			return !maneuverCodeToExclude?.includes(maneuverCode) && !isDisabledOb;
		});

		// Add predefined observations grouped by derived category
		const categoryMap: Record<string, IObservationRating[]> = {};
		filteredPredefinedObs?.forEach((obs) => {
			const category = obs?.observationCode?.split("_")[0];
			if (!categoryMap[category]) {
				categoryMap[category] = [];
			}
			categoryMap[category].push(obs);
		});

		// Overwrite or add new observations in the categoryMap
		filteredNewObs?.forEach((obs) => {
			const category = obs?.observationCode?.split("_")[0];
			const observations = categoryMap[category] || [];

			const uniqueObservations = observations?.filter(
				(existing) => existing.observationCode !== obs.observationCode || existing.maneuverCode !== obs.maneuverCode,
			);

			uniqueObservations.push(obs);
			categoryMap[category] = uniqueObservations;
		});

		// Consolidate observations by category, using newObs to overwrite predefinedObs when needed
		const _ratingB: { obCategoryCode: string; rating: number }[] = [];

		// Calculate the average rating per category
		for (const [category, observations] of Object.entries(categoryMap)) {
			const sumActiveOb = observations?.reduce((total, curr) => total + curr.activeAttempt, 0);

			const totalAttemptOb = observations?.reduce((total, curr) => total + curr.totalAttempt, 0);

			const categoryRating = sumActiveOb / totalAttemptOb;
			const rating = ratingScale(categoryRating * 100);
			observations?.forEach((obs) =>
				console.log(
					`Observation Code: ${obs.observationCode}, Maneuver Code: ${obs.maneuverCode}, Active Attempts: ${obs.activeAttempt}, Total Attempts: ${obs.totalAttempt}`,
				),
			);
			console.log(
				`Category: ${category}, sumActiveOb: ${sumActiveOb}, totalAttemptOb: ${totalAttemptOb},percentage : ${
					categoryRating * 100
				}, rating: ${rating}`,
			);
			_ratingB.push({ obCategoryCode: category, rating });
		}
		const ratingB = Math.round(_ratingB?.reduce((acc, { rating }) => acc + rating, 0) / _ratingB?.length);
		// Calculate the overall average rating
		console.log("Rating B : ", ratingB);
		return Number.isNaN(ratingB) ? 1 : ratingB;
	};

	calculateRatingC = (data: number[]) => {
		if (data?.length === 0) return 1;

		if (data?.includes(1)) return 1;

		const sum = data?.reduce((acc, prevValue) => acc + prevValue, 0) / data?.length;

		const decimalPart = +(sum % 1).toFixed(4);
		return decimalPart > 0.6 ? Math.ceil(sum) : Math.floor(sum);
	};

	ObservationBreakdownHowManyCalculation = ({
		newObs = [],
		predefinedObs = [],
		maneuverCodeToExclude = [],
		disabledObservations = [],
		formTypeCode,
		originalObsCodes,
	}: {
		predefinedObs: IObservationRating[];
		newObs: IObservationRating[];
		maneuverCodeToExclude?: string[];
		disabledObservations?: IPreviewDisabledObservation[];
		formTypeCode?: string;
		originalObsCodes?: Set<string>;
	}) => {
		if (this.isEligibleFormType(formTypeCode)) {
			if (resolveTs1RatingCalcKey(this.draftService.draft?.ratingFormVersion) === "v4") {
				return this.ts1V4Calc.ObservationBreakdownHowManyCalculation({
					newObs,
					predefinedObs,
					maneuverCodeToExclude,
					disabledObservations,
					originalObsCodes,
				});
			}
			return this.resolveV2Calculator().ObservationBreakdownHowManyCalculation({
				newObs,
				predefinedObs,
				maneuverCodeToExclude,
				disabledObservations,
				formTypeCode,
			});
		}

		//to exclude maneuver
		const filteredPredefinedObs = predefinedObs?.filter(({ maneuverCode, observationCode }) => {
			const isObservationDisabled = disabledObservations?.some(
				({ maneuverCode: disabledManeuverCode, observationCode: disabledObservationCode }) => {
					return disabledManeuverCode === maneuverCode && disabledObservationCode === observationCode;
				},
			);

			return !maneuverCodeToExclude?.includes(maneuverCode) && !isObservationDisabled;
		});

		const filteredNewObs = newObs?.filter(({ maneuverCode, observationCode }) => {
			const isObservationDisabled = disabledObservations?.some(
				({ maneuverCode: disabledManeuverCode, observationCode: disabledObservationCode }) => {
					return disabledManeuverCode === maneuverCode && disabledObservationCode === observationCode;
				},
			);

			return !maneuverCodeToExclude?.includes(maneuverCode) && !isObservationDisabled;
		});

		// Group observations by category
		const categoryMap: Record<string, IObservationRating[]> = {};

		// Add predefined observations grouped by derived category
		filteredPredefinedObs?.forEach((obs) => {
			const category = obs?.observationCode?.split("_")[0];
			if (!categoryMap[category]) {
				categoryMap[category] = [];
			}
			categoryMap[category].push(obs);
		});

		// Overwrite or add new observations in the categoryMap
		filteredNewObs?.forEach((obs) => {
			const category = obs?.observationCode?.split("_")[0];
			const observations = categoryMap[category] || [];

			const uniqueObservations = observations?.filter(
				(existing) => existing.observationCode !== obs.observationCode || existing.maneuverCode !== obs.maneuverCode,
			);

			uniqueObservations.push({
				observationCode: obs.observationCode,
				maneuverCode: obs.maneuverCode,
				totalAttempt: obs.totalAttempt,
				activeAttempt: obs.activeAttempt,
				isRemoved: obs.isRemoved,
			});
			categoryMap[category] = uniqueObservations;
		});

		// Calculate rating per category following TS1CalculateRatingA logic
		const categoryRatings: { obCategoryCode: string; rating: number }[] = [];

		for (const [category, observations] of Object.entries(categoryMap)) {
			// Get unique observation codes to avoid duplicates
			const predefinedObservationCodes = observations?.map((v) => v.observationCode);
			const uniquePredefinedObservationCodes = [...new Set(predefinedObservationCodes)];

			// Calculate additional observations (those not in predefined list)
			const _additionalObs =
				observations
					.filter(({ observationCode }) => !uniquePredefinedObservationCodes.includes(observationCode))
					?.filter(Boolean)?.length ?? 0;

			// Count removed observations on same category
			const _removedObsCount = observations?.filter(({ isRemoved }) => isRemoved)?.length ?? 0;

			const totalObs = _additionalObs + (predefinedObservationCodes?.length ?? 0);
			const categoryPercentage = Math.round(((totalObs - _removedObsCount) / totalObs) * 100);

			categoryRatings.push({
				obCategoryCode: category?.toUpperCase(),
				rating: ratingScale(categoryPercentage),
			});
		}

		return categoryRatings;
	};

	ObservationBreakdownHowOftenCalculation = ({
		newObs = [],
		predefinedObs = [],
		maneuverCodeToExclude = [],
		disabledObservations = [],
		formTypeCode,
		ratingAByCategory,
		orderedNewObs,
		originalObsCodes,
	}: {
		predefinedObs: IObservationRating[];
		newObs: IObservationRating[];
		maneuverCodeToExclude?: string[];
		disabledObservations?: IPreviewDisabledObservation[];
		formTypeCode?: string;
		ratingAByCategory?: Map<string, number>;
		orderedNewObs?: IObservationRating[];
		originalObsCodes?: Set<string>;
	}) => {
		if (this.isEligibleFormType(formTypeCode)) {
			if (resolveTs1RatingCalcKey(this.draftService.draft?.ratingFormVersion) === "v4") {
				return this.ts1V4Calc.ObservationBreakdownHowOftenCalculation({
					newObs,
					predefinedObs,
					maneuverCodeToExclude,
					disabledObservations,
					originalObsCodes,
				});
			}
			return this.resolveV2Calculator().ObservationBreakdownHowOftenCalculation({
				newObs,
				predefinedObs,
				maneuverCodeToExclude,
				disabledObservations,
				formTypeCode,
				ratingAByCategory,
				orderedNewObs,
			});
		}

		//to exclude maneuver
		const filteredPredefinedObs = predefinedObs?.filter(({ maneuverCode, observationCode }) => {
			const isObservationDisabled = disabledObservations?.some(
				({ maneuverCode: disabledManeuverCode, observationCode: disabledObservationCode }) => {
					return disabledManeuverCode === maneuverCode && disabledObservationCode === observationCode;
				},
			);

			return !maneuverCodeToExclude?.includes(maneuverCode) && !isObservationDisabled;
		});

		const filteredNewObs = newObs?.filter(({ maneuverCode, observationCode }) => {
			const isObservationDisabled = disabledObservations?.some(
				({ maneuverCode: disabledManeuverCode, observationCode: disabledObservationCode }) => {
					return disabledManeuverCode === maneuverCode && disabledObservationCode === observationCode;
				},
			);

			return !maneuverCodeToExclude?.includes(maneuverCode) && !isObservationDisabled;
		});

		// Add predefined observations grouped by derived category
		const categoryMap: Record<string, IObservationRating[]> = {};
		filteredPredefinedObs?.forEach((obs) => {
			const category = obs?.observationCode?.split("_")[0];
			if (!categoryMap[category]) {
				categoryMap[category] = [];
			}
			categoryMap[category].push(obs);
		});

		// Overwrite or add new observations in the categoryMap
		filteredNewObs?.forEach((obs) => {
			const category = obs?.observationCode?.split("_")[0];
			const observations = categoryMap[category] || [];

			const uniqueObservations = observations?.filter(
				(existing) => existing.observationCode !== obs.observationCode || existing.maneuverCode !== obs.maneuverCode,
			);

			uniqueObservations.push(obs);
			categoryMap[category] = uniqueObservations;
		});

		// Calculate breakdown per category
		const categoryBreakdown: {
			obCategoryCode: string;
			rating: number;
			totalAttempts: number;
			activeAttempts: number;
			completionPercentage: number;
			observationCount: number;
		}[] = [];

		// Calculate the rating per category with detailed breakdown
		for (const [category, observations] of Object.entries(categoryMap)) {
			const sumActiveOb = observations?.reduce((total, curr) => total + curr.activeAttempt, 0);

			const totalAttemptOb = observations?.reduce((total, curr) => total + curr.totalAttempt, 0);

			const completionPercentage = sumActiveOb / totalAttemptOb;
			const rating = ratingScale(completionPercentage * 100);

			categoryBreakdown.push({
				obCategoryCode: category?.toUpperCase(),
				rating,
				totalAttempts: totalAttemptOb,
				activeAttempts: sumActiveOb,
				completionPercentage: +(completionPercentage * 100).toFixed(2),
				observationCount: observations.length,
			});
		}

		return categoryBreakdown;
	};
}
